import { animate, state, style, transition, trigger } from '@angular/animations';
import { NestedTreeControl } from '@angular/cdk/tree';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  InputSignal,
  model,
  ModelSignal,
  output,
  OutputEmitterRef,
  untracked,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { IconComponent } from '@libs/components/icons';
import { NavItemModel } from '@libs/shared/models';
import { NavLinkDirective } from '@libs/shared/ui';

@Component({
  selector: 'ui-upkeep-navigation',
  templateUrl: './upkeep-navigation.component.html',
  styleUrls: ['./upkeep-navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatTreeModule,
    NavLinkDirective,
    NgTemplateOutlet,
    IconComponent,
  ],
  animations: [
    trigger('slideVertical', [
      state('*', style({
        height: 0,
      })),
      state('show', style({
        height: '*',
      })),
      transition('* => *', [animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)')]),
    ]),
  ],
})
export class UpkeepNavigationComponent {
  public navigate: OutputEmitterRef<void> = output();

  public data: InputSignal<readonly NavItemModel[]> = input.required();
  public disableRouter: InputSignal<boolean> = input(false);
  public expandFirstLevel: InputSignal<boolean> = input(true);
  public current: ModelSignal<NavItemModel | undefined> = model();

  public treeControl: NestedTreeControl<NavItemModel, NavItemModel> = new NestedTreeControl<NavItemModel>((node: NavItemModel) => node.children);
  public dataSource: MatTreeNestedDataSource<NavItemModel> = new MatTreeNestedDataSource<NavItemModel>();

  public constructor() {
    effect(() => void this.expandNode(this.current()));
    effect(() => void this.updateData(this.data() as NavItemModel[]));
  }

  public hasChild = (_: number, node: NavItemModel): boolean => !!node.children && node.children.length > 0;

  public isCurrent(item: NavItemModel): boolean {
    return item.type === this.current()?.type
      && item.id === this.current()?.id
      && item.name === this.current()?.name;
  }

  public clickNavItem(node: NavItemModel): void {
    this.navigate.emit();
    this.current.set(node);
  }

  private updateData(data: NavItemModel[]): void {
    if (this.expandFirstLevel()) {
      data.forEach(node => void this.treeControl.expand(node));
    }

    this.expandNode(untracked(this.current));
    this.dataSource.data = data;
  }

  private expandNode(node?: NavItemModel): void {
    while (node) {
      this.treeControl.expand(node);
      node = node.parent;
    }
  }
}
